import api from 'services/api';
import { array, enforce, num, object, runSchema, str } from 'validation';

// Source of truth for the wire shape: nap-backend
// `routes/scheduling_windows.js` GET /scheduling_windows. The full
// response shape is `{ nodes, pagination, config, filtersConfig }`
// — only `nodes` is consumed here, and each node carries far more
// fields than this LOV needs (timezone, schedules, references, etc).
// The projection narrows to the identifiers that an LOV row actually
// renders.
//
// This deliberately calls `api` directly instead of going through the
// `getSchedulingWindows` endpoint, which is the one place in this resource that
// breaks the endpoint/operation layering. Reason: the endpoint validates the full
// contract — every window's rules and links, ~20 fields each — and this call asks
// for a thousand rows to fill a dropdown that renders three identifiers. Paying
// full boundary validation on data nobody reads is the cost the layering is
// supposed to buy something for, and here it buys nothing. Same documented gap as
// the zones vertical's `getAuthorizedZones` (#990). If this ever migrates to
// server-side search, fold it back onto the endpoint at the same time.

export interface SchedulingWindowSummary {
  readonly scheduling_window_id: number;
  readonly name: string;
  readonly zone_name: string;
}

const summarySchema = object<SchedulingWindowSummary>({
  scheduling_window_id: num(),
  name: str(),
  zone_name: str(),
});

interface SchedulingWindowsResponse {
  readonly nodes: readonly SchedulingWindowSummary[];
}

const responseSchema = object<SchedulingWindowsResponse>({
  nodes: array(summarySchema),
});

const LOV_LIMIT = 1000;

/**
 * Fetch all scheduling windows the current user can see. No params:
 * pagination isn't applied here — the LOV needs the complete set so
 * Typeahead's client-side filter has every option to match against.
 * If the universe of scheduling windows ever grows past a UX-bearable
 * dropdown size, this is the call site to migrate to server-side
 * search.
 */
export const getSchedulingWindowsLOV = async (): Promise<readonly SchedulingWindowSummary[]> => {
  const res = await api.get('/scheduling_windows', {
    params: { limit: LOV_LIMIT },
  });
  const parsed = runSchema(responseSchema, res.data, 'getSchedulingWindowsLOV');
  if (!parsed.ok) {
    throw new Error('getSchedulingWindowsLOV: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['nodes']).nodes;
};
